import React from 'react';

export type ToothSurface = 'M' | 'D' | 'O' | 'B' | 'L';

interface ApexoRadialMenuProps {
  x: number;
  y: number;
  toothId: string;
  selectedSurfaces: ToothSurface[];
  onToggleSurface: (surface: ToothSurface) => void;
  onClose: () => void;
}

const SURFACES: { id: ToothSurface; label: string; angle: number }[] = [
  { id: 'O', label: 'O', angle: 0 },    // Top
  { id: 'M', label: 'M', angle: 72 },   // Right-ish
  { id: 'B', label: 'B', angle: 144 },  // Bottom-right
  { id: 'L', label: 'L', angle: 216 },  // Bottom-left
  { id: 'D', label: 'D', angle: 288 },  // Left-ish
];

export default function ApexoRadialMenu({
  x,
  y,
  toothId,
  selectedSurfaces,
  onToggleSurface,
  onClose
}: ApexoRadialMenuProps) {
  const radius = 50;

  return (
    <>
      {/* Invisible backdrop to catch outside clicks */}
      <div 
        style={{ position: 'fixed', inset: 0, zIndex: 9998 }} 
        onClick={onClose}
        onContextMenu={(e) => { e.preventDefault(); onClose(); }}
      />
      
      <div 
        className="apexo-radial-container"
        style={{
          position: 'fixed',
          left: x,
          top: y,
          zIndex: 9999,
          transform: 'translate(-50%, -50%)',
          width: radius * 2 + 40,
          height: radius * 2 + 40,
          pointerEvents: 'none'
        }}
      >
        {/* Center Tooth Label */}
        <div style={{
          position: 'absolute',
          left: '50%',
          top: '50%',
          transform: 'translate(-50%, -50%)',
          background: 'white',
          borderRadius: '50%',
          width: '36px',
          height: '36px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontWeight: 800,
          color: '#334155',
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          pointerEvents: 'auto',
          zIndex: 2
        }}>
          {toothId.replace(/^(UR|UL|LR|LL)/, '')}
        </div>

        {/* Orbiting buttons */}
        {SURFACES.map((surf) => {
          const rad = (surf.angle - 90) * (Math.PI / 180); // -90 to start at top
          const btnX = Math.cos(rad) * radius;
          const btnY = Math.sin(rad) * radius;
          const isSelected = selectedSurfaces.includes(surf.id);
          
          return (
            <button
              key={surf.id}
              onClick={(e) => {
                e.stopPropagation();
                onToggleSurface(surf.id);
              }}
              style={{
                position: 'absolute',
                left: `calc(50% + ${btnX}px)`,
                top: `calc(50% + ${btnY}px)`,
                transform: 'translate(-50%, -50%)',
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                border: isSelected ? '2px solid #0ea5e9' : '1px solid #cbd5e1',
                background: isSelected ? '#e0f2fe' : 'white',
                color: isSelected ? '#0369a1' : '#64748b',
                fontWeight: 700,
                fontSize: '0.8rem',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: isSelected ? '0 0 10px rgba(14, 165, 233, 0.4)' : '0 2px 4px rgba(0,0,0,0.1)',
                pointerEvents: 'auto',
                transition: 'all 0.2s',
                zIndex: 1
              }}
              onMouseOver={(e) => {
                if (!isSelected) {
                  e.currentTarget.style.transform = 'translate(-50%, -50%) scale(1.1)';
                }
              }}
              onMouseOut={(e) => {
                if (!isSelected) {
                  e.currentTarget.style.transform = 'translate(-50%, -50%) scale(1)';
                }
              }}
            >
              {surf.label}
            </button>
          );
        })}
      </div>
    </>
  );
}
